import { zodResolver } from '@hookform/resolvers/zod';
import { Add } from 'iconsax-react';
import { isNil } from 'rambda';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { FilePreview } from '@/components/common/file-preview';
import FormInput from '@/components/common/form-input';
import ProgressButton from '@/components/common/progress-button';
import { FieldMessage } from '@/components/fields/field-message';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import { convertFileToUploadable, uploadableScheme } from '@/models/uploadable';
import { addToast } from '@/stores/toast';
import { FileType } from '@/utils/file-type';

export default function FeedbackRoute() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const {
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
    control,
    formState: { errors, isValid },
  } = useForm<FeedbackFormData>({
    resolver: zodResolver(feedbackFormScheme),
    defaultValues: {
      content: '',
      contact: '',
      screenshot: null,
    },
  });

  const screenshot = watch('screenshot');
  const describeTextCount = watch('content')?.length ?? 0;

  const handleSubmitFeedback = async () => {
    setIsLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 400));
    setIsLoading(false);
    addToast({
      type: 'success',
      message: t('feedback.gratitude'),
    });
    reset();
  };

  return (
    <PageLayout
      title={t('general-settings.feedback')}
      className='gap-0'
      headerConfig={{
        navigateBack: true,
      }}>
      <form
        onSubmit={handleSubmit(handleSubmitFeedback)}
        className='mx-5 flex flex-col gap-4'>
        <label
          htmlFor='contact'
          className='form-control'>
          <div className='label'>
            <span className='label-text'>{t('feedback.contact')}</span>
          </div>

          <FormInput
            id='contact'
            control={control}
            name='contact'
            placeholder={t('feedback.contact-placeholder')}
          />
        </label>

        <label
          htmlFor='content'
          className='form-control'>
          <div className='label'>
            <span className='label-text'>{t('feedback.content')}</span>
          </div>

          <textarea
            id='content'
            {...register('content')}
            placeholder={t('feedback.content-placeholder')}
            className='textarea textarea-bordered h-32 w-full resize-none bg-transparent focus:border-primary focus:outline-none'
          />

          <FieldMessage
            name='content'
            errors={errors}
          />
          <div className='flex-1 text-right text-xs text-secondary'>
            {describeTextCount}
            /200
          </div>
        </label>

        <label
          htmlFor='screenshot'
          className='form-control'>
          <div className='label'>
            <span className='label-text'>{t('feedback.screenshot')}</span>
          </div>

          <input
            ref={inputRef}
            type='file'
            className='hidden'
            accept={FileType.Image}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                setValue('screenshot', convertFileToUploadable(file));
              }
            }}
          />

          {!isNil(screenshot) ? (
            <FilePreview
              fileType={FileType.Image}
              file={screenshot}
            />
          ) : (
            <button
              type='button'
              className='flex size-32 items-center justify-center rounded-lg border-2 border-dashed'
              onClick={() => inputRef.current?.click()}>
              <Add className='size-10 text-secondary' />
            </button>
          )}

          <FieldMessage
            name='screenshot'
            errors={errors}
          />
        </label>

        <ProgressButton
          as='submit'
          isLoading={isLoading}
          disabled={!isValid}
          className='btn btn-primary fixed inset-x-5 bottom-safe-offset-5'
          label='common.submit'
        />
      </form>
    </PageLayout>
  );
}

export const Component = FeedbackRoute;

const feedbackFormScheme = z.object({
  content: z.string().min(1, 'errors.string.length|count=1'),
  screenshot: uploadableScheme.nullable(),
  contact: z.string().min(1, 'errors.string.length|count=1'),
});

type FeedbackFormData = z.infer<typeof feedbackFormScheme>;
